/**
 * Admin Center, Reiter „Chat" (TEAM-CHAT.md, 7, 8, 10 und 11).
 *
 * Drei Dinge, die der Betrieb festlegt und nicht der Code: wer mitliest,
 * wohin Anhänge gehen und wie weit der Verlauf offen liegt. Alle drei
 * ändern sich mit der Belegschaft und mit der Ablage – die Anwendung
 * ändert sich dabei nicht.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import type { ChatEinrichtung } from '../typen';

const ARTEN: { key: 'team' | 'direkt' | 'vorgang'; label: string; erklaerung: string }[] = [
  { key: 'team', label: 'Team-Chat', erklaerung: 'Anhänge aus dem Chat für alle.' },
  { key: 'direkt', label: 'Direktnachrichten', erklaerung: 'Anhänge aus Gesprächen unter vier Augen.' },
  { key: 'vorgang', label: 'Kommentare am Vorgang', erklaerung: 'Anhänge an Angeboten und Aufträgen.' },
];

export function ChatVerwaltung() {
  const { toastZeigen } = useKora();
  const queryClient = useQueryClient();
  const abfrage = useQuery({
    queryKey: ['chat-einrichtung'],
    queryFn: () => api.hole<ChatEinrichtung>('/chat/einrichtung'),
  });

  const [pfade, setPfade] = useState<Record<string, string>>({});
  useEffect(() => {
    if (abfrage.data) setPfade(abfrage.data.ablage);
  }, [abfrage.data]);

  const speichern = useMutation({
    mutationFn: (daten: Record<string, unknown>) => api.ersetze<{ ok: boolean }>('/chat/einrichtung', daten),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chat-einrichtung'] });
      void queryClient.invalidateQueries({ queryKey: ['chat-ueberblick'] });
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Konnte nicht gespeichert werden.'),
  });

  const d = abfrage.data;
  if (!d) return <div className="modal-hinweis">Wird geladen …</div>;

  /** Die Vorschau setzt heutiges Datum und Beispielwerte ein. */
  const beispiel = (art: string) => {
    const jetzt = new Date();
    return (pfade[art] ?? '')
      .replace(/\{Jahr\}/g, String(jetzt.getFullYear()))
      .replace(/\{Monat\}/g, String(jetzt.getMonth() + 1).padStart(2, '0'))
      .replace(/\{Nutzer\}/g, 'Martin')
      .replace(/\{Vorgangsnummer\}/g, '26014_FE');
  };

  return (
    <div className="cv">
      {/* --- Wer ist dabei -------------------------------------------- */}
      <div className="modal-abschnitt">
        <div className="modal-abschnitt-titel">Wer am Chat teilnimmt</div>
        <p className="modal-hinweis">
          Nur Büro. Monteure und Werkstatt lesen nicht mit – wer hier ausgehakt ist, sieht weder
          Team-Chat noch Direktnachrichten und taucht in der Teilnehmerliste nicht auf.
        </p>
        <div className="cv-leute">
          {d.teilnehmer.map((t) => (
            <label className="cv-person" key={t.name}>
              <input
                type="checkbox"
                checked={t.aktiv}
                onChange={(e) => speichern.mutate({ teilnehmer: { [t.name]: e.target.checked } })}
              />
              <span className="cv-person-name">{t.name}</span>
              <span className="cv-person-voll">{t.anzeigename}</span>
            </label>
          ))}
        </div>
      </div>

      {/* --- Ablage ---------------------------------------------------- */}
      <div className="modal-abschnitt">
        <div className="modal-abschnitt-titel">Wohin Anhänge in der Ablage gehen</div>
        <p className="modal-hinweis">
          Keine Datei liegt in Kora. Der Verfasser nennt den Pfad, bevor jemand etwas anhängt.
          Platzhalter: <code>{'{Jahr}'}</code> <code>{'{Monat}'}</code> <code>{'{Nutzer}'}</code>{' '}
          <code>{'{Vorgangsnummer}'}</code>.
        </p>
        {ARTEN.map((a) => (
          <div className="cv-pfad" key={a.key}>
            <label className="cv-pfad-feld">
              <span className="cv-pfad-label">
                {a.label}
                <span className="cv-pfad-erklaerung">{a.erklaerung}</span>
              </span>
              <input
                value={pfade[a.key] ?? ''}
                placeholder={d.vorgabe[a.key]}
                onChange={(e) => setPfade((p) => ({ ...p, [a.key]: e.target.value }))}
                onBlur={() => {
                  if (pfade[a.key] !== d.ablage[a.key]) {
                    speichern.mutate({ ablage: { [a.key]: pfade[a.key] ?? '' } });
                  }
                }}
              />
            </label>
            <span className="cv-pfad-vorschau">Ergibt heute: {beispiel(a.key) || '—'}</span>
          </div>
        ))}
      </div>

      {/* --- Aufbewahrung ---------------------------------------------- */}
      <div className="modal-abschnitt">
        <div className="modal-abschnitt-titel">Wie weit der Verlauf offen liegt</div>
        <p className="modal-hinweis">
          Sichtbar ist beim Öffnen nur diese Spanne; alles davor holt ein Klick. Was älter ist als
          die zweite Zahl, zeigt das Panel nicht mehr – gelöscht wird nie, auch nicht danach.
        </p>
        <div className="cv-zahlen">
          <label className="ein-feld">
            <span>Sichtbar … Tage</span>
            <input
              type="number"
              className="ein-eingabe ein-zahl"
              defaultValue={d.aufbewahrung.sichtbarTage}
              min={1}
              max={30}
              onBlur={(e) => {
                const w = Number(e.target.value);
                if (w >= 1 && w !== d.aufbewahrung.sichtbarTage) speichern.mutate({ sichtbarTage: w });
              }}
            />
          </label>
          <label className="ein-feld">
            <span>Ins Archiv nach … Tagen</span>
            <input
              type="number"
              className="ein-eingabe ein-zahl"
              defaultValue={d.aufbewahrung.archivTage}
              min={1}
              max={3650}
              onBlur={(e) => {
                const w = Number(e.target.value);
                if (w >= 1 && w !== d.aufbewahrung.archivTage) speichern.mutate({ archivTage: w });
              }}
            />
          </label>
        </div>
      </div>
    </div>
  );
}
